"use client"

import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"

import { cn } from "@/lib/utils"

const SLIDER_TONE_CLASS = {
  default: {
    track: "bg-secondary",
    range: "bg-primary",
    thumb:
      "h-5 w-5 border-2 border-primary bg-background ring-offset-background focus-visible:ring-ring",
  },
  panel: {
    track: "h-1 bg-white/12",
    range: "bg-white",
    thumb:
      "h-7 w-7 border-0 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.35)] ring-offset-neutral-900 focus-visible:ring-white/40",
  },
} as const;

type SliderTone = keyof typeof SLIDER_TONE_CLASS;

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    tone?: SliderTone;
  }
>(({ className, tone = "default", ...props }, ref) => {
  const thumbCount =
    (Array.isArray(props.value) && props.value.length) ||
    (Array.isArray(props.defaultValue) && props.defaultValue.length) ||
    1;
  const toneClass = SLIDER_TONE_CLASS[tone];

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn("relative flex w-full touch-none select-none items-center", className)}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn("relative w-full grow overflow-hidden rounded-full", toneClass.track)}
      >
        <SliderPrimitive.Range className={cn("absolute h-full", toneClass.range)} />
      </SliderPrimitive.Track>
      {Array.from({ length: thumbCount }).map((_, index) => (
        <SliderPrimitive.Thumb
          key={index}
          className={cn(
            "block rounded-full transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
            toneClass.thumb,
          )}
        />
      ))}
    </SliderPrimitive.Root>
  );
})
Slider.displayName = SliderPrimitive.Root.displayName

export { Slider }

